import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker, MatDatepickerToggle } from '@angular/material/datepicker';
import { LanguageType } from '@shared/models/common/language-type.enum';
import { LanguageService } from '@shared/services/language.service';
// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import { Moment } from 'moment';
import { CalendarViewType } from '../../models/common/calendar-view-type.enum';
import { YearPickerHeaderComponent } from './year-picker-header/year-picker-header.component';

const moment = _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const YEAR_MONTH_FORMATS = {
  parse: {
    dateInput: 'MMMM, YYYY',
  },
  display: {
    dateInput: 'MMMM, YYYY',
    monthYearLabel: 'MMMM, YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM, YYYY',
  },
};

@Component({
  selector: 'app-year-month-picker',
  templateUrl: './year-month-picker.component.html',
  styleUrls: ['./year-month-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    // { provide: MAT_DATE_FORMATS, useValue: YEAR_MONTH_FORMATS },
  ]
})
export class YearMonthPickerComponent {

  private _language: string;
  @Input() set language(value: string){
    this._language = value;
    this.language ? this.dateAdapter.setLocale(this.language) : this.dateAdapter.setLocale(this.languageService.languageType);
  }
  
  get language() {
    return this._language;
  }

  @Input() id: string;
  @Input() control: FormControl;
  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string;
  @Input() readonly: boolean;
  @Input() required: boolean;
  @Input() set initDate(date: any) {
    this.control.setValue(!!date ? moment.utc(date) : undefined);
  }
  @Input() minDate: any;
  @Input() maxDate: any;
  @Input() header: any;
  @Output() selectedDate = new EventEmitter<any>();

  yearPickerHeader = YearPickerHeaderComponent;
  selectedYear: number;

  constructor(private dateAdapter: DateAdapter<Date>, private languageService: LanguageService) {
    this.language ? this.dateAdapter.setLocale(this.language) : this.dateAdapter.setLocale(this.languageService.languageType);
  }
  
  chosenYearHandler(normalizedYear: Moment): void {
    this.selectedYear = normalizedYear.year();
  }

  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>, toggleBtn: any): void {
    const ctrlValue = this.control.value ? this.control.value : moment();
    const selectedYear = this.selectedYear ===
      normalizedMonth.year() ? this.selectedYear : normalizedMonth.year();
    ctrlValue.year(selectedYear);
    ctrlValue.month(normalizedMonth.month());
    ctrlValue.date(1).startOf('day');
    this.control.setValue(ctrlValue);
    this.control.markAsTouched(); // todo is this still needed ?
    this.control.markAsDirty(); // todo is this still needed ?
    this.selectedDate.emit(this.control.value);
    datepicker.close();
    this.restoreFocus(toggleBtn);
  }

  clearDate(ctrl: FormControl) {
    ctrl.patchValue(undefined);
  }

  // mat-datepicker bug - fails to restore focused element - which is the datepicker toggle button,
  // for 'monthSelected' handler after datepicker is closed, therefore we try to mimic the behaviour programmatically
  // reference: Ln586 @ https://github.com/angular/components/blob/master/src/material/datepicker/datepicker-base.ts
  private restoreFocus(focusedToggleBtn: MatDatepickerToggle<any>): void {
    const getHtmlElementOf = (matToggleBtn: MatDatepickerToggle<any>): HTMLElement | null => {
      return matToggleBtn?._button?._elementRef?.nativeElement;
    };

    const completeClose = (): void => {
      focusedToggleBtnBeforeOpen.focus();
    };

    const focusedToggleBtnBeforeOpen = getHtmlElementOf(focusedToggleBtn);
    if (focusedToggleBtnBeforeOpen && typeof focusedToggleBtnBeforeOpen.focus === 'function') {
      setTimeout(completeClose);
    }
  }

  get headerComponent() {
    return this.header === CalendarViewType.Year && this.yearPickerHeader ?
      this.yearPickerHeader : null;
  }

}
